import type { FormState, UseFormHandleSubmit } from "react-hook-form";
import { useAuth } from "@src/common/hooks/useAuth";
import { useActionBy } from "@src/common/hooks/useActionBy";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import type { Shipment } from "@api/graphql/generated/generated-types";
import type { ManualArrivalFormData } from "../types";
import { formatManualArrivalFormToTaskArrivalOverrides } from "../utils/formatManualArrivalFormToTaskArrivalOverrides";
import { useForceArriveForShipmentsMutation } from "./useForceArriveForShipmentsMutation";

interface UseManualArrivalSubmitParams {
  shipment: Shipment | undefined;
  sortedStops: NonNullable<Shipment["stops"]>;
  isShipmentAssigned: boolean;
  onClose: () => void;
  handleSubmit: UseFormHandleSubmit<ManualArrivalFormData>;
  dirtyFields: FormState<ManualArrivalFormData>["dirtyFields"];
}

export function useManualArrivalSubmit({
  shipment,
  sortedStops,
  isShipmentAssigned,
  onClose,
  handleSubmit,
  dirtyFields,
}: UseManualArrivalSubmitParams) {
  const { courierId } = useAuth();
  const { updatedBy } = useActionBy();
  const { showToast } = useCourieStore();
  const [forceArriveForShipments, { loading }] =
    useForceArriveForShipmentsMutation();

  const onSubmit = handleSubmit((data) => {
    if (!courierId || !shipment?.id || !isShipmentAssigned || !updatedBy)
      return;

    const arrivalOverrides = formatManualArrivalFormToTaskArrivalOverrides(
      sortedStops,
      data,
      dirtyFields,
    );

    if (arrivalOverrides.length === 0) {
      showToast({
        type: "error",
        message:
          "Change at least one arrival from its current value, or cancel.",
      });
      return;
    }

    forceArriveForShipments({
      variables: {
        courierId,
        updatedBy,
        shipmentArrivalOverrides: [
          {
            shipmentId: shipment.id,
            arrivalOverrides,
          },
        ],
      },
    })
      .then(() => {
        showToast({ type: "success", message: "Arrival times saved successfully" });
        onClose();
      })
      .catch((error) => {
        showErrorToast(error, showToast);
      });
  });

  return { onSubmit, loading };
}
